import { Box, Paper, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import RefreshButton from "../../components/button/RefreshButton";
import { MainContainer } from "../../components/container/MainContainer";
import { PageTitleContainer } from "../../components/container/PageTitleContainer";
import ResizableBox from "../../components/container/ResizableBox";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableHeaderToolsContainer } from "../../components/container/TableHeaderToolsContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import ContextMenu from "../../components/context-menu/ContextMenu";
import ExportAsXLSXButton from "../../components/export/ExportAsXLSXButton";
import AgGrid from "../../components/grid/AgGrid";
import { defaultColDef, floatFormater, leftAlignColDef, rightAlignColDef } from "../../components/grid/columnDefinitions/columnDefinitions";
import SearchBox from "../../components/input/SearchBox";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import { Process } from "../../db_access/process";
import { Product } from "../../db_access/product";
import Footprint from "../../environment-page-components/Footprint";
import SankeyDiagram from "../../environment-page-components/SankeyDiagram";
import SystemBoundary from "../../environment-page-components/SystemBoundary";

const ProductArchive = () => {
  const { t } = useTranslation();
  const { refresh, setRefresh, selectedLanguage } = useContextProvider();
  const gridRef = useRef(null);
  const gridHeaderContainerRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [processData, setProcessData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    Product.get_my_company_archive()
      .then((res) => res.json())
      .then((data) => setProducts(data));
  }, [refresh]);

  useEffect(() => {
    if (selectedProducts.length === 0) {
      setProcessData([]);
    } else {
      Process.get_by_product_ids(selectedProducts.map((i) => i.id))
        .then((res) => res.json())
        .then((data) => setProcessData(data));
    }
  }, [selectedProducts]);

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.setGridOption("quickFilterText", searchText);
    }
  }, [searchText]);

  const unarchiveProduct = (selectedProducts) => {
    Product.unarchive(selectedProducts.map((s) => s.id)).finally(() => {
      setSelectedProducts([]);
      setRefresh((r) => !r);
    });
  };

  const deleteProduct = (selectedProducts) => {
    Product.delete(selectedProducts.map((s) => s.id)).finally(() => {
      setSelectedProducts([]);
      setRefresh((r) => !r);
    });
  };

  const onSelectionChanged = (event) => {
    const selected = event.api.getSelectedRows();
    setSelectedProducts(selected);
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    setMenuPosition({ x: e.pageX, y: e.pageY });
    setMenuVisible(true);
  };

  const handleCloseMenu = () => {
    setMenuVisible(false);
  };

  const colDefs = [
    { field: "id", hide: true },
    {
      headerName: "Name",
      field: "name",
      headerCheckboxSelection: true,
      cellStyle: { fontWeight: 500, ...leftAlignColDef.cellStyle },
      ...defaultColDef,
    },
    {
      headerName: "Supplier",
      field: "supplier",
      ...defaultColDef,
      ...leftAlignColDef,
    },
    {
      headerName: "Year",
      field: "year",
      ...defaultColDef,
      ...leftAlignColDef,
    },
    {
      headerName: "GWP (kg CO₂e)",
      field: "gwp",
      ...rightAlignColDef,
      ...defaultColDef,
      ...floatFormater,
    },
  ];

  const contextMenuOptions = [
    { label: "Unarchive", action: unarchiveProduct, needSelectedItems: true },
    { label: "Delete", action: deleteProduct, needSelectedItems: true },
  ];

  return (
    <MainContainer>
      <PageTitleContainer>
        <Box>
          <Typography variant="Page Title">{t("Product Archive")}</Typography>
          <Typography variant="Page Subtitle">{t("View and manage your archived products")}</Typography>
        </Box>
      </PageTitleContainer>

      <Paper variant="Table Container">
        <TableHeaderContainer>
          <TableTitleContainer>
            <Typography variant="Table Title">
              {t("Archived Products")} ({products.length})
            </Typography>
          </TableTitleContainer>
          <TableHeaderToolsContainer ref={gridHeaderContainerRef}>
            <SearchBox text={searchText} setText={setSearchText} />
            <ExportAsXLSXButton data={products} label="Export as .xlsx" filename="archived_products.xlsx" />
            <RefreshButton />
          </TableHeaderToolsContainer>
        </TableHeaderContainer>
        <Box>
          <ResizableBox>
            <AgGrid ref={gridRef} columnDefs={colDefs} rowData={products} onSelectionChanged={onSelectionChanged} handleContextMenu={handleContextMenu} headerCheckbox={false} />
          </ResizableBox>
        </Box>
      </Paper>

      <Box sx={{ paddingY: 1 }} />

      <Box sx={{ display: selectedProducts.length === 1 ? "block" : "none" }}>
        <SystemBoundary processData={processData} selectedLanguage={selectedLanguage} />
        <Footprint processData={processData} selectedLanguage={selectedLanguage} pageType="supplier" />
        <SankeyDiagram processData={processData} selectedLanguage={selectedLanguage} selectedRow={selectedProducts[0]} />
      </Box>

      <ContextMenu x={menuPosition.x} y={menuPosition.y} visible={menuVisible} onClose={handleCloseMenu} selectedItems={selectedProducts} options={contextMenuOptions} />
    </MainContainer>
  );
};

export default ProductArchive;
